import type { Result } from '@/stores/type/result.type'
import type { DatasetType } from '@/views/calculations/type/resultView.type'
import type { ChartConfiguration } from '@/stores/type/chartConfiguration.type'
import {
  CHART_AXIS_GROUPINGS,
  CHART_DISPLAY_MODES,
  CHART_SORT_ORDERS,
  DEFAULT_CHART_AXIS_GROUPING,
  DEFAULT_DISPLAY_MODE,
  DEFAULT_SELECTED_PHASES,
  DEFAULT_SORT_ORDER,
  STACK_DIMENSION_BY_DISPLAY_MODE,
  LIFECYCLE_PHASE_GROUP_COLORS,
  LIFECYCLE_PHASE_GROUP_OPTIONS
} from '@/constants/chartConfiguration'
import {
  collectDimensionValues,
  dimensionValues,
  resolveTrajectoryIndices,
  buildTrajectLabels,
  buildMaterialCategories,
  type TrajectoryIndexByName
} from '@/views/calculations/utils/resultChart/dimensions'
import {
  getMaterialColor,
  buildMaterialColorMap,
  type MaterialColorByName
} from '@/views/calculations/utils/resultChart/colors'
import { phaseGroupValue, sumSelectedPhases } from '@/views/calculations/utils/resultChart/phases'
import { applyRowOverrides } from '@/views/calculations/utils/resultChart/results'

export const buildDimensionDatasets = (
  results: Result[],
  trajectLabels: string[],
  xDim: string = DEFAULT_CHART_AXIS_GROUPING,
  stackDim: string = CHART_AXIS_GROUPINGS.MATERIAL,
  selectedPhases: string[] = DEFAULT_SELECTED_PHASES,
  colorMap?: MaterialColorByName
): DatasetType[] => {
  const trajectoryByName: TrajectoryIndexByName = new Map()
  trajectLabels.forEach((label, index) => trajectoryByName.set(label, index))

  const stackKeys = collectDimensionValues(results, stackDim)
  const groupedData = new Map<string, number[]>()
  for (const stackKey of stackKeys) {
    groupedData.set(stackKey, Array(trajectLabels.length).fill(0))
  }

  for (const result of results) {
    const value = sumSelectedPhases(result, selectedPhases)
    const xIndices = resolveTrajectoryIndices(result, xDim, trajectoryByName)
    if (!xIndices.length) continue
    for (const stackKey of dimensionValues(result, stackDim)) {
      const dataArray = groupedData.get(stackKey)
      if (!dataArray) continue
      for (const xIndex of xIndices) {
        dataArray[xIndex] += value
      }
    }
  }

  const datasets: DatasetType[] = []
  for (const stackKey of stackKeys) {
    const data = groupedData.get(stackKey)
    if (!data || !data.some((value) => value !== 0)) continue
    datasets.push({
      label: stackKey,
      data,
      backgroundColor: colorMap?.get(stackKey) ?? getMaterialColor(stackKey),
      stack: 'stack-0'
    })
  }

  return datasets
}

export const buildPhaseDatasets = (
  results: Result[],
  trajectLabels: string[],
  selectedPhases: string[],
  grouping: string = DEFAULT_CHART_AXIS_GROUPING,
  displayMode: string = CHART_DISPLAY_MODES.PHASE_STACK
): DatasetType[] => {
  const groups = selectedPhases.length ? selectedPhases : DEFAULT_SELECTED_PHASES
  const orderedGroups = LIFECYCLE_PHASE_GROUP_OPTIONS.filter((option) =>
    groups.includes(option.value)
  )

  const trajectoryByName: TrajectoryIndexByName = new Map()
  trajectLabels.forEach((label, index) => trajectoryByName.set(label, index))

  const groupedData = new Map<string, number[]>()
  for (const group of orderedGroups) {
    groupedData.set(group.value, Array(trajectLabels.length).fill(0))
  }

  for (const result of results) {
    const indices = resolveTrajectoryIndices(result, grouping, trajectoryByName)
    for (const index of indices) {
      for (const group of orderedGroups) {
        groupedData.get(group.value)![index] += phaseGroupValue(result, group.value)
      }
    }
  }

  const grouped = displayMode === CHART_DISPLAY_MODES.PHASE_GROUPED
  const datasets: DatasetType[] = []
  for (const group of orderedGroups) {
    const data = groupedData.get(group.value)!
    if (!data.some((value) => value !== 0)) continue
    datasets.push({
      label: group.title,
      data,
      backgroundColor: LIFECYCLE_PHASE_GROUP_COLORS[group.value] ?? '#888888',
      stack: grouped ? group.value : 'stack-0'
    })
  }

  return datasets
}

export const appendTransparentTotalDataset = (
  datasets: DatasetType[],
  trajectoryCount: number
): DatasetType[] => {
  return [
    ...datasets,
    {
      label: '',
      data: Array(trajectoryCount).fill(0), // heights set by syncTotalHeights after filtering/sort
      backgroundColor: 'rgba(0,0,0,0)',
      stack: 'total', // own stack: do NOT stack on top of the real segments
      grouped: false, // overlay the real column instead of a side-by-side bar
      isTotal: true
    }
  ]
}

export const syncTotalHeights = (datasets: DatasetType[], columnCount: number): void => {
  const total = datasets.find((dataset) => dataset.isTotal)
  if (!total) return
  const tops = new Array<number>(columnCount).fill(0)
  for (const dataset of datasets) {
    if (dataset.isTotal) continue
    for (let index = 0; index < columnCount; index += 1) {
      const value = Number(dataset.data[index] ?? 0)
      if (value > 0) tops[index] += value
    }
  }
  total.data = tops
}

export const filterDatasetColumns = (
  datasets: DatasetType[],
  allLabels: string[],
  visibleLabels: string[]
): DatasetType[] => {
  const visibleIndices: number[] = []

  for (let labelIndex = 0; labelIndex < allLabels.length; labelIndex += 1) {
    const label = allLabels[labelIndex]
    if (visibleLabels.includes(label)) {
      visibleIndices.push(labelIndex)
    }
  }

  return datasets.map((dataset) => ({
    ...dataset,
    data: visibleIndices.map((visibleIndex) => dataset.data[visibleIndex] ?? 0)
  }))
}

export const applyColumnSort = (
  labels: string[],
  datasets: DatasetType[],
  sortOrder: string
): { labels: string[]; datasets: DatasetType[] } => {
  if (sortOrder !== CHART_SORT_ORDERS.ASC && sortOrder !== CHART_SORT_ORDERS.DESC) {
    return { labels, datasets }
  }

  const columnCount = labels.length
  const totals = new Array<number>(columnCount).fill(0)

  for (const dataset of datasets) {
    if (dataset.isTotal) continue
    for (let index = 0; index < columnCount; index += 1) {
      totals[index] += Number(dataset.data[index] ?? 0)
    }
  }

  const order = Array.from({ length: columnCount }, (_unused, index) => index)
  order.sort((a, b) =>
    sortOrder === CHART_SORT_ORDERS.ASC ? totals[a] - totals[b] : totals[b] - totals[a]
  )

  return {
    labels: order.map((index) => labels[index]),
    datasets: datasets.map((dataset) => ({
      ...dataset,
      data: order.map((index) => dataset.data[index] ?? 0)
    }))
  }
}

export const assembleChartData = (params: {
  results: Result[]
  config: ChartConfiguration
  categoryFilters?: string[]
  groupFilters?: string[]
}): { labels: string[]; datasets: DatasetType[]; categories: string[] } => {
  const { results, config, categoryFilters, groupFilters } = params
  const grouping = config.x_axis_grouping ?? DEFAULT_CHART_AXIS_GROUPING
  const configResults = applyRowOverrides(results, config)
  const labels = buildTrajectLabels(configResults, grouping)
  const categories = buildMaterialCategories(configResults)
  const mode = config.display_mode ?? DEFAULT_DISPLAY_MODE
  const selectedGroups = groupFilters ?? labels

  const selectedCategories =
    categoryFilters ??
    (config.filter_material_categories?.length
      ? config.filter_material_categories.filter((category) => categories.includes(category))
      : categories)

  const isPhase =
    mode === CHART_DISPLAY_MODES.PHASE_STACK || mode === CHART_DISPLAY_MODES.PHASE_GROUPED
  const filtered = configResults.filter((result) =>
    selectedCategories.includes(result.material_display_name)
  )

  let datasetsWithTotal: DatasetType[]
  if (isPhase) {
    const datasets = buildPhaseDatasets(
      filtered,
      labels,
      config.selected_phases ?? DEFAULT_SELECTED_PHASES,
      grouping,
      mode
    )
    datasetsWithTotal =
      mode === CHART_DISPLAY_MODES.PHASE_GROUPED
        ? datasets
        : appendTransparentTotalDataset(datasets, labels.length)
  } else {
    const stackDim = STACK_DIMENSION_BY_DISPLAY_MODE[mode] ?? CHART_AXIS_GROUPINGS.MATERIAL
    const colorMap = buildMaterialColorMap(collectDimensionValues(configResults, stackDim))
    const datasets = buildDimensionDatasets(
      filtered,
      labels,
      grouping,
      stackDim,
      config.selected_phases ?? DEFAULT_SELECTED_PHASES,
      colorMap
    )
    datasetsWithTotal = appendTransparentTotalDataset(datasets, labels.length)
  }

  const visibleLabels = labels.filter((label) => selectedGroups.includes(label))
  const columnFiltered = filterDatasetColumns(datasetsWithTotal, labels, visibleLabels)
  const sorted = applyColumnSort(
    visibleLabels,
    columnFiltered,
    config.sort_order ?? DEFAULT_SORT_ORDER
  )
  syncTotalHeights(sorted.datasets, sorted.labels.length)

  return { labels: sorted.labels, datasets: sorted.datasets, categories: selectedCategories }
}
